import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  Step,
  WalkthroughDialogComponent,
  WalkthroughNavigationHelperService,
  WalkthroughPostComponent,
  walkthroughSanitizedVideoResolver,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';

@Injectable({ providedIn: 'root' })
export class CollaborativeHiringWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly gettingStartedFacade = inject(GettingStartedFacade);
  private readonly navigationHelperService = inject(WalkthroughNavigationHelperService);

  private readonly destroyRef = inject(DestroyRef);

  public readonly subtask = GettingStartedSubtask.CollaborativeHiring;

  private steps: Step[] = [
    {
      id: 'walkthrough-step-collaborative-hiring-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_1.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_1.description',
          ),
          videoUrl: walkthroughSanitizedVideoResolver('https://www.youtube.com/embed/bPWEBmbmTSQ'),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_1.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-collaborative-hiring-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['top', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'top' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-collaborative-hiring-3',
      position: 3,

      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['top', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_3.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_3.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'top' }],
        offset: { offsetX: 89, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-collaborative-hiring-4',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_4.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_4.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-collaborative-hiring-5',
      position: 5,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_5.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_5.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-collaborative-hiring-6',
      position: 6,
      component: {
        definition: WalkthroughPostComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_6.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_6.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.collaborative_hiring.step_6.cta_text',
          ),
          helpArticleKey: 'settingUpTeam',
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(state, this.subtask, ['candidates']);
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  private finishHandler(): void {
    this.router.navigate(['candidates']);
  }
}
